import { find, memoize } from 'lodash'
import { createSelector, createSelectorCreator } from 'reselect'

import { RootState } from '@/store'
import { getUsers } from '@/store/selectors'
import getAttributePathParts from '@/utilities/getAttributePathParts'
import { getAttributes } from '@/utilities/UserUtilities/selectors'

const getPublisher = (state: RootState) => state.publisher
export const getDraft = (state: RootState) => getPublisher(state).draft
const getAudience = (state: RootState) => getPublisher(state).audience
export const getBulkTargetingStatus = (state: RootState) => getPublisher(state).bulkTargeting
const getTargetingSuggestions = (state: RootState) => getPublisher(state).suggestions
const getUserById = (users: ReturnType<typeof getUsers>, id: string) => users.byId[id]
const getUserByEmail = (users: ReturnType<typeof getUsers>, email: string) =>
  find(users.byId, { email: email.toLowerCase() })
const getUserDisplayName = (user: any) => user.displayName
const getUserEmail = (user: any) => user.email
const getUserName = (user: any) => user.name
const getUserNickName = (user: any) => user.nickName

export const makeGetTargetingAggregateSuggestions = () => {
  return createSelector(getTargetingSuggestions, ({ aggregates }) => {
    return Object.keys(aggregates).reduce(
      (results, urn) => {
        results[urn] = Object.values(aggregates[urn])
        return results
      },
      {} as Record<string, any>
    )
  })
}

export const makeGetTargetingRuleSetSuggestions = () => {
  return createSelector([getTargetingSuggestions, getUsers], ({ ruleSets }, users) => {
    return Object.keys(ruleSets).reduce(
      (results, ruleSet) => {
        results[ruleSet] = ruleSets[ruleSet].map((userId: string) => users.byId[userId])
        return results
      },
      {} as Record<string, any>
    )
  })
}

// use an unbounded cache with key's of email address as they're guaranteed to be unique
const getUserNameFallback = createSelectorCreator(memoize, (displayName, name, nickName, email) => email.toLowerCase())(
  [getUserDisplayName, getUserName, getUserNickName, getUserEmail],
  (displayName, name, nickName, email) => {
    if (displayName) return displayName

    if (name) {
      const { familyName, formatted, givenName } = name

      if (familyName) {
        if (nickName) return `${nickName} ${familyName}`
        else if (givenName) return `${givenName} ${familyName}`
      }

      return formatted || email
    } else {
      return email
    }
  }
)

const getReadableAudienceMember = (member: any, attributes: any[], users: ReturnType<typeof getUsers>) => {
  let displayType = member.type
  let displayValue = member.display

  if (member.type === 'email') {
    const user = getUserByEmail(users, member.value)

    if (user) displayValue = getUserNameFallback(user)
  } else if (member.type === 'guid') {
    const user = getUserById(users, member.value)

    if (user) displayValue = getUserNameFallback(user)
  } else {
    const pathParts = getAttributePathParts(member.urn.toLowerCase())
    const attribute = attributes.find(
      ({ scimAttributeName, scimSchema }) =>
        scimSchema.toLowerCase() === pathParts[0] && scimAttributeName.toLowerCase() === pathParts[1]
    )

    if (attribute) displayType = attribute.displayName
  }

  return { ...member, displayType, displayValue }
}

export const makeGetReadableAudience = () => {
  return createSelector([getAudience, getAttributes, getUsers], (audience, attributes, users) =>
    audience.map(member => getReadableAudienceMember(member, attributes, users))
  )
}
